/**
 * ECS (Elastic Container Service) Monitor Component
 * Monitor ECS clusters, services, tasks, deployments, and container health.
 * Executive-grade dashboard using shared monitoring design system.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  Paper,
  Collapse,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Cloud as CloudIcon,
  Storage as StorageIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';
import apiService from '../../services/api';
import {
  getMonitoringColors,
  mapStatus,
  monoValueSx,
  serviceRowSx,
} from './monitoringDesignSystem';
import {
  MonitoringPageContainer,
  MonitoringHeroBanner,
  MonitoringKPICard,
  MonitoringChartCard,
  MonitoringDonutGauge,
  AutoRefreshIndicator,
} from './MonitoringComponents';
import TimeRangeFilter from '../common/TimeRangeFilter';
import useTimeRangeParam from '../common/useTimeRangeParam';
import { HOUR_PRESETS, HourToken } from '../common/timeRange';

// ---------------------------------------------------------------------------
// Interfaces (preserved)
// ---------------------------------------------------------------------------

interface ECSSummary {
  totalClusters: number;
  totalServices: number;
  runningTasks: number;
  stoppedTasks: number;
  failedTasks: number;
  activeAlarms: number;
  healthStatus: 'healthy' | 'degraded' | 'unhealthy';
  lastUpdated: string;
}

interface ECSCluster {
  name: string;
  arn: string;
  status: string;
  registeredContainerInstancesCount: number;
  runningTasksCount: number;
  pendingTasksCount: number;
  activeServicesCount: number;
  statistics?: any[];
  tags?: any[];
}

interface ECSService {
  serviceName: string;
  serviceArn: string;
  clusterArn: string;
  status: string;
  desiredCount: number;
  runningCount: number;
  pendingCount: number;
  launchType: string;
  taskDefinition: string;
  deployments: any[];
  healthCheckGracePeriodSeconds?: number;
  createdAt: string;
  tags?: any[];
}

interface ECSTask {
  taskArn: string;
  clusterArn: string;
  taskDefinitionArn: string;
  lastStatus: string;
  desiredStatus: string;
  launchType: string;
  cpu?: string;
  memory?: string;
  containers: any[];
  startedAt?: string;
  stoppedAt?: string;
  stoppedReason?: string;
  stopCode?: string;
  healthStatus?: string;
  tags?: any[];
}

interface ECSDeployment {
  id: string;
  serviceName: string;
  clusterArn: string;
  status: string;
  taskDefinition: string;
  desiredCount: number;
  runningCount: number;
  pendingCount: number;
  failedTasks?: number;
  createdAt: string;
  updatedAt?: string;
  launchType: string;
  rolloutState?: string;
  rolloutStateReason?: string;
}

interface ECSAlarm {
  name: string;
  description?: string;
  state: string;
  reason: string;
  namespace: string;
  metricName: string;
  dimensions: any[];
  threshold: number;
  comparisonOperator: string;
  lastUpdate: string;
  actionsEnabled: boolean;
}

interface ECSDashboard {
  summary: ECSSummary;
  clusters: ECSCluster[];
  services: ECSService[];
  recentTasks: ECSTask[];
  alarms: ECSAlarm[];
}

type ServiceHealth = 'green' | 'yellow' | 'red';

const getServiceHealth = (service: ECSService): ServiceHealth => {
  const statusUp = service.status?.toUpperCase() === 'ACTIVE';
  if (!statusUp || (service.desiredCount > 0 && service.runningCount === 0)) return 'red';
  if (service.runningCount < service.desiredCount || service.pendingCount > 0) return 'yellow';
  return 'green';
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ECSMonitor: React.FC = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const c = getMonitoringColors(mode);
  const dispatch = useDispatch<AppDispatch>();

  // --- State (preserved) ---
  const [dashboard, setDashboard] = useState<ECSDashboard | null>(null);
  // Hour-scale window: the SAME pill control as every other screen, over this
  // screen's own vocabulary. `resolveRange()` speaks calendar days only, so the
  // token is handed to the existing fetch logic untouched.
  const [timeRange, setTimeRange] = useTimeRangeParam<HourToken>(
    { type: '24h' },
    { presets: HOUR_PRESETS }
  );
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [expandedCluster, setExpandedCluster] = useState<string | null>(null);
  const [expandedService, setExpandedService] = useState<string | null>(null);

  // --- Data loading (preserved) ---
  const loadECSData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getECSMonitorDashboard({ timeRange: timeRange.type });
      const dashboardData = (response as any).data || response;
      setDashboard(dashboardData);
    } catch (error) {
      console.error('Failed to load ECS monitoring data:', error);
      dispatch(addNotification({ type: 'error', message: 'Failed to load ECS monitoring data' }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadECSData();
  }, [timeRange]);

  useEffect(() => {
    const interval = setInterval(loadECSData, 60000);
    return () => clearInterval(interval);
  }, [timeRange]);

  // --- Utility functions (preserved) ---
  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    const upper = status?.toUpperCase();
    if (upper === 'ACTIVE' || upper === 'RUNNING' || upper === 'SUCCESS') return 'success';
    if (upper === 'PENDING' || upper === 'PROVISIONING') return 'warning';
    if (upper === 'STOPPED' || upper === 'FAILED' || upper === 'ALARM') return 'error';
    return 'default';
  };

  const getStatusIcon = (status: string) => {
    const upper = status?.toUpperCase();
    if (upper === 'ACTIVE' || upper === 'RUNNING') return <CheckCircleIcon sx={{ color: c.healthy }} fontSize="small" />;
    if (upper === 'PENDING') return <WarningIcon sx={{ color: c.warning }} fontSize="small" />;
    if (upper === 'STOPPED' || upper === 'FAILED') return <ErrorIcon sx={{ color: c.critical }} fontSize="small" />;
    return <CloudIcon fontSize="small" />;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const handleToggleCluster = (clusterName: string) => {
    setExpandedCluster(expandedCluster === clusterName ? null : clusterName);
  };

  const handleToggleService = (serviceArn: string) => {
    setExpandedService(expandedService === serviceArn ? null : serviceArn);
  };

  // --- Computed ---
  const summary = dashboard?.summary;
  const clusters = dashboard?.clusters || [];
  const services = useMemo(() =>
    [...(dashboard?.services || [])].sort((a, b) => b.runningCount - a.runningCount),
    [dashboard?.services]
  );
  const recentTasks = dashboard?.recentTasks || [];
  const alarms = useMemo(() =>
    [...(dashboard?.alarms || [])].sort((a, b) =>
      new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime()
    ),
    [dashboard?.alarms]
  );

  const { greenServices, yellowServices, redServices } = useMemo(() => {
    const green: ECSService[] = [];
    const yellow: ECSService[] = [];
    const red: ECSService[] = [];
    for (const svc of services) {
      const health = getServiceHealth(svc);
      if (health === 'red') red.push(svc);
      else if (health === 'yellow') yellow.push(svc);
      else green.push(svc);
    }
    return { greenServices: green, yellowServices: yellow, redServices: red };
  }, [services]);

  const overallHealth = mapStatus(
    redServices.length > 0 ? 'red' : yellowServices.length > 0 ? 'yellow' : 'green'
  );
  const bannerLabel =
    overallHealth === 'healthy' ? 'ALL SERVICES OPERATIONAL' :
    overallHealth === 'warning' ? 'SERVICES DEGRADED' : 'SERVICES DOWN';
  const bannerSummary =
    `${greenServices.length}/${services.length} services running across ${clusters.length} cluster${clusters.length !== 1 ? 's' : ''}`;

  if (loading && !dashboard) {
    return (
      <MonitoringPageContainer>
        <LinearProgress />
        <Typography sx={{ mt: 2, color: c.textSecondary }}>Loading ECS monitoring data...</Typography>
      </MonitoringPageContainer>
    );
  }

  return (
    <MonitoringPageContainer>
      {/* Hero Banner */}
      <MonitoringHeroBanner
        status={overallHealth}
        title={bannerLabel}
        subtitle={bannerSummary}
        actions={
          <>
            <TimeRangeFilter
              value={timeRange}
              onChange={setTimeRange}
              presets={HOUR_PRESETS}
              hideCustom
            />
            <AutoRefreshIndicator
              autoRefresh={autoRefresh}
              onToggle={() => setAutoRefresh(!autoRefresh)}
              onRefresh={loadECSData}
            />
          </>
        }
      />

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <MonitoringKPICard
            label="Total Services"
            value={services.length}
            accentColor={c.textPrimary}
            icon={<StorageIcon sx={{ fontSize: 32 }} />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MonitoringKPICard
            label="Healthy"
            value={greenServices.length}
            accentColor={c.healthy}
            icon={<CheckCircleIcon sx={{ fontSize: 32 }} />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MonitoringKPICard
            label="Degraded"
            value={yellowServices.length}
            accentColor={yellowServices.length > 0 ? c.warning : c.textMuted}
            icon={<WarningIcon sx={{ fontSize: 32 }} />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MonitoringKPICard
            label="Down"
            value={redServices.length}
            accentColor={redServices.length > 0 ? c.critical : c.textMuted}
            icon={<ErrorIcon sx={{ fontSize: 32 }} />}
          />
        </Grid>
      </Grid>

      {/* Health Distribution Donut + Cluster Summary + Alerts */}
      {services.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <MonitoringChartCard title="Service Health">
              <MonitoringDonutGauge
                data={[
                  { label: 'Healthy', value: greenServices.length, color: c.healthy },
                  { label: 'Degraded', value: yellowServices.length, color: c.warning },
                  { label: 'Down', value: redServices.length, color: c.critical },
                ]}
                centerValue={services.length}
                centerLabel="services"
              />
            </MonitoringChartCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <MonitoringChartCard title="Cluster Overview">
              <Grid container spacing={2}>
                {clusters.map((cluster) => {
                  const isHealthy = cluster.pendingTasksCount === 0 && cluster.runningTasksCount > 0;
                  const accentColor = isHealthy ? c.healthy : c.warning;
                  return (
                    <Grid item xs={12} key={cluster.arn}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: `1px solid ${c.cardBorder}`,
                          borderLeft: `4px solid ${accentColor}`,
                          bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          {isHealthy ? (
                            <CheckCircleIcon sx={{ color: accentColor, fontSize: 20 }} />
                          ) : (
                            <WarningIcon sx={{ color: accentColor, fontSize: 20 }} />
                          )}
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: c.textPrimary }}>{cluster.name}</Typography>
                        </Box>
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <Typography variant="caption" sx={{ color: c.textMuted, fontSize: '0.65rem', textTransform: 'uppercase' }}>Services</Typography>
                            <Typography variant="h6" sx={{ ...monoValueSx, color: c.textPrimary }}>{cluster.activeServicesCount}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption" sx={{ color: c.textMuted, fontSize: '0.65rem', textTransform: 'uppercase' }}>Running</Typography>
                            <Typography variant="h6" sx={{ ...monoValueSx, color: c.healthy }}>{cluster.runningTasksCount}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption" sx={{ color: c.textMuted, fontSize: '0.65rem', textTransform: 'uppercase' }}>Pending</Typography>
                            <Typography variant="h6" sx={{ ...monoValueSx, color: cluster.pendingTasksCount > 0 ? c.warning : c.textSecondary }}>{cluster.pendingTasksCount}</Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  );
                })}
                {clusters.length === 0 && (
                  <Grid item xs={12}>
                    <Alert severity="info">No clusters found</Alert>
                  </Grid>
                )}
              </Grid>
            </MonitoringChartCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <MonitoringChartCard title="Alerts">
              {redServices.length > 0 && (
                <Alert
                  severity="error"
                  icon={<ErrorIcon />}
                  sx={{ mb: 2, borderRadius: 2, borderLeft: `4px solid ${c.critical}`, bgcolor: c.criticalBg }}
                >
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {redServices.length} service{redServices.length !== 1 ? 's' : ''} DOWN
                  </Typography>
                  {redServices.map((svc) => (
                    <Box key={svc.serviceArn} sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                      <ErrorIcon fontSize="small" />
                      <Typography variant="body2">
                        <strong>{svc.serviceName}</strong> — {svc.runningCount}/{svc.desiredCount} tasks
                        {svc.status?.toUpperCase() !== 'ACTIVE' && ` (${svc.status})`}
                      </Typography>
                    </Box>
                  ))}
                </Alert>
              )}

              {yellowServices.length > 0 && (
                <Alert
                  severity="warning"
                  icon={<WarningIcon />}
                  sx={{ mb: 2, borderRadius: 2, borderLeft: `4px solid ${c.warning}`, bgcolor: c.warningBg }}
                >
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {yellowServices.length} service{yellowServices.length !== 1 ? 's' : ''} degraded
                  </Typography>
                  {yellowServices.map((svc) => (
                    <Box key={svc.serviceArn} sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                      <WarningIcon fontSize="small" />
                      <Typography variant="body2">
                        <strong>{svc.serviceName}</strong> — {svc.runningCount}/{svc.desiredCount} tasks
                        {svc.pendingCount > 0 && `, ${svc.pendingCount} pending`}
                      </Typography>
                    </Box>
                  ))}
                </Alert>
              )}

              {summary && summary.healthStatus === 'degraded' && redServices.length === 0 && yellowServices.length === 0 && (
                <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2, borderRadius: 2 }}>
                  <Typography variant="body2">
                    All services running normally. {summary.failedTasks > 0 ? `${summary.failedTasks} recent failed task${summary.failedTasks !== 1 ? 's' : ''}` : ''}{summary.failedTasks > 0 && summary.activeAlarms > 0 ? ' and ' : ''}{summary.activeAlarms > 0 ? `${summary.activeAlarms} active alarm${summary.activeAlarms !== 1 ? 's' : ''}` : ''} detected.
                  </Typography>
                </Alert>
              )}

              {summary && summary.healthStatus === 'unhealthy' && redServices.length === 0 && yellowServices.length === 0 && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2, bgcolor: c.criticalBg }}>
                  <Typography variant="body2">
                    <strong>Health Status: Unhealthy</strong> — {summary.failedTasks} failed tasks and {summary.activeAlarms} active alarms
                  </Typography>
                </Alert>
              )}

              {redServices.length === 0 && yellowServices.length === 0 && (!summary || summary.healthStatus === 'healthy') && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CheckCircleIcon sx={{ fontSize: 48, color: c.healthy, mb: 1 }} />
                  <Typography variant="body1" sx={{ color: c.textPrimary, fontWeight: 600 }}>
                    All Systems Nominal
                  </Typography>
                  <Typography variant="caption" sx={{ color: c.textMuted }}>
                    No alerts or issues detected
                  </Typography>
                </Box>
              )}
            </MonitoringChartCard>
          </Grid>
        </Grid>
      )}

      {/* Clusters Table */}
      <Box sx={{ mb: 3 }}>
        <MonitoringChartCard title={`ECS Clusters (${clusters.length})`}>
          <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'transparent' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: c.textSecondary }}>Cluster Name</TableCell>
                  <TableCell sx={{ color: c.textSecondary }}>Status</TableCell>
                  <TableCell align="right" sx={{ color: c.textSecondary }}>Services</TableCell>
                  <TableCell align="right" sx={{ color: c.textSecondary }}>Running Tasks</TableCell>
                  <TableCell align="right" sx={{ color: c.textSecondary }}>Pending Tasks</TableCell>
                  <TableCell align="right" sx={{ color: c.textSecondary }}>Instances</TableCell>
                  <TableCell sx={{ color: c.textSecondary }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clusters.map((cluster) => (
                  <React.Fragment key={cluster.arn}>
                    <TableRow hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getStatusIcon(cluster.status)}
                          <Typography variant="body2" sx={{ color: c.textPrimary }}>{cluster.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={cluster.status} color={getStatusColor(cluster.status)} size="small" />
                      </TableCell>
                      <TableCell align="right" sx={{ color: c.textPrimary }}>{cluster.activeServicesCount}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ ...monoValueSx, color: c.healthy }}>{cluster.runningTasksCount}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ ...monoValueSx, color: cluster.pendingTasksCount > 0 ? c.warning : c.textSecondary }}>
                          {cluster.pendingTasksCount}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ color: c.textPrimary }}>{cluster.registeredContainerInstancesCount}</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleToggleCluster(cluster.name)}>
                          {expandedCluster === cluster.name ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ p: 0 }}>
                        <Collapse in={expandedCluster === cluster.name} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="body2" sx={{ color: c.textSecondary }}>
                              <strong>ARN:</strong> {cluster.arn}
                            </Typography>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
                {clusters.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" sx={{ color: c.textSecondary }}>No clusters found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </MonitoringChartCard>
      </Box>

      {/* Services Table */}
      <Box sx={{ mb: 3 }}>
        <MonitoringChartCard title={`ECS Services (${services.length})`}>
          <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'transparent' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40, color: c.textSecondary }}></TableCell>
                  <TableCell sx={{ color: c.textSecondary }}>Service Name</TableCell>
                  <TableCell sx={{ color: c.textSecondary }}>Status</TableCell>
                  <TableCell sx={{ color: c.textSecondary }}>Launch Type</TableCell>
                  <TableCell align="right" sx={{ color: c.textSecondary }}>Desired</TableCell>
                  <TableCell align="right" sx={{ color: c.textSecondary }}>Running</TableCell>
                  <TableCell align="right" sx={{ color: c.textSecondary }}>Pending</TableCell>
                  <TableCell sx={{ color: c.textSecondary }}>Deployments</TableCell>
                  <TableCell sx={{ color: c.textSecondary }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services.map((service) => {
                  const health = getServiceHealth(service);
                  const healthStatus = mapStatus(health);
                  return (
                    <React.Fragment key={service.serviceArn}>
                      <TableRow hover sx={serviceRowSx(healthStatus, mode)}>
                        <TableCell sx={{ pl: 2 }}>
                          {health === 'green' && <CheckCircleIcon sx={{ color: c.healthy, fontSize: 20 }} />}
                          {health === 'yellow' && <WarningIcon sx={{ color: c.warning, fontSize: 20 }} />}
                          {health === 'red' && <ErrorIcon sx={{ color: c.critical, fontSize: 20 }} />}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" sx={{ color: c.textPrimary }}>{service.serviceName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={service.status} color={getStatusColor(service.status)} size="small" />
                        </TableCell>
                        <TableCell sx={{ color: c.textPrimary }}>{service.launchType}</TableCell>
                        <TableCell align="right" sx={{ color: c.textPrimary }}>{service.desiredCount}</TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{ ...monoValueSx, color: service.runningCount === service.desiredCount ? c.healthy : c.warning }}
                          >
                            {service.runningCount}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ ...monoValueSx, color: service.pendingCount > 0 ? c.warning : c.textSecondary }}>
                            {service.pendingCount}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={`${service.deployments?.length || 0} active`} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleToggleService(service.serviceArn)}>
                            {expandedService === service.serviceArn ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={9} sx={{ p: 0 }}>
                          <Collapse in={expandedService === service.serviceArn} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="caption" sx={{ color: c.textSecondary }}><strong>Task Definition:</strong></Typography>
                                  <Typography variant="body2" sx={{ color: c.textPrimary }}>{service.taskDefinition}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="caption" sx={{ color: c.textSecondary }}><strong>Created:</strong></Typography>
                                  <Typography variant="body2" sx={{ color: c.textPrimary }}>{formatDate(service.createdAt)}</Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
                {services.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" sx={{ color: c.textSecondary }}>No services found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </MonitoringChartCard>
      </Box>

      {/* Recent Stopped Tasks */}
      {recentTasks.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <MonitoringChartCard title={`Recent Stopped Tasks (${recentTasks.length})`}>
            <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'transparent' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: c.textSecondary }}>Task ARN</TableCell>
                    <TableCell sx={{ color: c.textSecondary }}>Status</TableCell>
                    <TableCell sx={{ color: c.textSecondary }}>Launch Type</TableCell>
                    <TableCell sx={{ color: c.textSecondary }}>Stopped Reason</TableCell>
                    <TableCell sx={{ color: c.textSecondary }}>Stopped At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentTasks.slice(0, 10).map((task) => (
                    <TableRow key={task.taskArn} hover>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200, color: c.textPrimary, fontFamily: '"Fira Code", monospace', fontSize: '0.8rem' }}>
                          {task.taskArn.split('/').pop()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={task.lastStatus} color={getStatusColor(task.lastStatus)} size="small" />
                      </TableCell>
                      <TableCell sx={{ color: c.textPrimary }}>{task.launchType}</TableCell>
                      <TableCell>
                        <Tooltip title={task.stoppedReason || 'N/A'}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 300, color: c.textSecondary }}>
                            {task.stoppedReason || 'N/A'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ color: c.textMuted }}>{formatDate(task.stoppedAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </MonitoringChartCard>
        </Box>
      )}

      {/* Active Alarms */}
      {alarms.length > 0 && (
        <MonitoringChartCard title={`Active ECS Alarms (${alarms.length})`}>
          <Alert severity="warning" sx={{ mb: 2, bgcolor: c.warningBg }}>
            {alarms.length} ECS-related alarm{alarms.length !== 1 ? 's' : ''} currently in ALARM state
          </Alert>
          <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'transparent' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: c.textSecondary }}>Alarm Name</TableCell>
                  <TableCell sx={{ color: c.textSecondary }}>Metric</TableCell>
                  <TableCell sx={{ color: c.textSecondary }}>State</TableCell>
                  <TableCell sx={{ color: c.textSecondary }}>Reason</TableCell>
                  <TableCell sx={{ color: c.textSecondary }}>Last Update</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {alarms.map((alarm) => (
                  <TableRow key={alarm.name} hover>
                    <TableCell sx={{ color: c.textPrimary }}>{alarm.name}</TableCell>
                    <TableCell sx={{ color: c.textPrimary }}>{alarm.metricName}</TableCell>
                    <TableCell>
                      <Chip label={alarm.state} color={getStatusColor(alarm.state)} size="small" icon={<ErrorIcon />} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={alarm.reason}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 300, color: c.textSecondary }}>
                          {alarm.reason}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ color: c.textMuted }}>{formatDate(alarm.lastUpdate)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </MonitoringChartCard>
      )}

      {/* Last Updated */}
      {summary && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Typography variant="caption" sx={{ color: c.textMuted }}>
            Last updated: {formatDate(summary.lastUpdated)}
          </Typography>
        </Box>
      )}
    </MonitoringPageContainer>
  );
};

export default ECSMonitor;
